import type { StateCreator, StoreMutatorIdentifier } from 'zustand'

/**
 * Pinia `computed` → Zustand derived fields.
 *
 * Wrap a store creator so `derive(state)` is re-run after every `set`, and its
 * result merged into the state. Consumers then read derived values as plain
 * fields with the same names the Pinia store exposed:
 *
 *   export const useAuthStore = create<AuthState>()(
 *     derived<AuthState>((s) => ({
 *       isAuthenticated: !!s.user,
 *       isSuperAdmin: s.userProfile?.isSuperAdmin || false
 *     }))((set, get) => ({ ...state and actions... }))
 *   )
 *
 * Keep `derive` pure and cheap: it runs on every update.
 */
export function derived<T extends object> (derive: (state: T) => Partial<T>) {
  return <Mps extends [StoreMutatorIdentifier, unknown][] = [], Mcs extends [StoreMutatorIdentifier, unknown][] = []>(
    config: StateCreator<T, Mps, Mcs>
  ): StateCreator<T, Mps, Mcs> =>
    (set, get, api) => {
      const apply = (partial: any, replace?: any) =>
        (set as any)((state: T) => {
          const next = typeof partial === 'function' ? partial(state) : partial
          const merged = { ...state, ...next } as T
          return { ...next, ...derive(merged) }
        }, replace)
      ;(api as any).setState = apply
      const initial = config(apply as any, get, api)
      return { ...initial, ...derive(initial) }
    }
}
